import { useIsomorphicLayoutEffect } from 'react-use';

import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type UseUI2026StylesParams = {
  /**
   * False when this shell is not the 2026 chrome (mobile, classic settings, legacy public board).
   * Omitted on layouts that are that chrome. The flag alone is not enough: mobile keeps it on.
   */
  enabled?: boolean;
  /**
   * Keep `data-ui2026` on even when the LaunchDarkly flag is off. Used by the desktop workspace
   * shell, which always mounts 2026 chrome.
   */
  force?: boolean;
};

/**
 * Enables `ui2026:` Tailwind utilities by setting `data-ui2026` on `<html>`.
 * `_document` starts with the attribute so the first paint matches the default-on flag.
 * A shell that is not 2026 chrome removes it after mount. Unmount does not: the replacement
 * shell (`HomeLayout`, legacy `PublicBoard`) is what clears it.
 */
export const useUI2026Styles = ({ enabled = true, force = false }: UseUI2026StylesParams = {}) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const applyUi2026Styles = enabled && (force || ui2026);

  useIsomorphicLayoutEffect(() => {
    const htmlElement = document.documentElement;

    if (applyUi2026Styles) {
      htmlElement.setAttribute('data-ui2026', '');
    } else {
      htmlElement.removeAttribute('data-ui2026');
    }
  }, [applyUi2026Styles]);
};
